/**
 * Shared push notification API service factory (T109a).
 *
 * Accepts a fetch-compatible function from each frontend's API client,
 * decoupling the push service from frontend-specific API implementations.
 */

import {
  type NotificationPreference,
  type PushSubscribeRequest,
  type PushSubscriptionResponse,
  transformPreferences,
  transformSubscription,
  transformSubscriptionList,
  transformVapidKey,
  type UnsubscribeResponse,
  type VapidKeyResponse,
} from './types';

/**
 * Fetch-compatible function signature.
 * Each frontend wraps its API client (api.get/api.post/api.put) into this shape.
 */
type ApiFetch = (path: string, init?: { method?: string; body?: unknown }) => Promise<unknown>;

export interface PushService {
  getVapidKey: () => Promise<VapidKeyResponse>;
  subscribe: (request: PushSubscribeRequest) => Promise<PushSubscriptionResponse>;
  unsubscribe: (endpoint: string) => Promise<UnsubscribeResponse>;
  listSubscriptions: () => Promise<PushSubscriptionResponse[]>;
  getPreferences: () => Promise<NotificationPreference[]>;
  updatePreferences: (preferences: NotificationPreference[]) => Promise<NotificationPreference[]>;
}

export function createPushService(apiFetch: ApiFetch): PushService {
  return {
    async getVapidKey(): Promise<VapidKeyResponse> {
      const raw = await apiFetch('/push/vapid-key');
      return transformVapidKey(raw as Record<string, unknown>);
    },

    async subscribe(request: PushSubscribeRequest): Promise<PushSubscriptionResponse> {
      const body: Record<string, unknown> = {
        endpoint: request.endpoint,
        p256dh: request.p256dh,
        auth: request.auth,
      };
      if (request.userAgent !== undefined) {
        body.user_agent = request.userAgent;
      }
      const raw = await apiFetch('/push/subscribe', { method: 'POST', body });
      return transformSubscription(raw as Record<string, unknown>);
    },

    async unsubscribe(endpoint: string): Promise<UnsubscribeResponse> {
      const raw = await apiFetch('/push/unsubscribe', {
        method: 'POST',
        body: { endpoint },
      });
      return raw as UnsubscribeResponse;
    },

    async listSubscriptions(): Promise<PushSubscriptionResponse[]> {
      const raw = await apiFetch('/push/subscriptions');
      return transformSubscriptionList(raw as Record<string, unknown>);
    },

    async getPreferences(): Promise<NotificationPreference[]> {
      const raw = await apiFetch('/push/preferences');
      return transformPreferences(raw as Record<string, unknown>);
    },

    async updatePreferences(preferences: NotificationPreference[]): Promise<NotificationPreference[]> {
      const raw = await apiFetch('/push/preferences', {
        method: 'PUT',
        body: { preferences },
      });
      return transformPreferences(raw as Record<string, unknown>);
    },
  };
}

/**
 * Convert a URL-safe base64 VAPID key to a Uint8Array for pushManager.subscribe().
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
